import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { requireFeature } from "@/lib/feature-gate";
import instance from "@/lib/axios";

import { MyProjectTable } from "@/components/crud/table/user/MyProjectTable";
import { UserProjectFormSheet } from "@/components/crud/forms/user/UserProjectFormSheet";
import { ProjectDetailsSheet } from "@/components/crud/forms/ProjectDetailsSheet";
import { useAuthStore } from "@/stores/useAuthStore";
import useFetchProjects from "@/hooks/api/projects/useFetchProject";
import useCreateProject from "@/hooks/api/projects/useCreateProject";
import useUpdateProject from "@/hooks/api/projects/useUpdateProject";
import useDeleteProject from "@/hooks/api/projects/useDeleteProject";
import useAddProjectMember from "@/hooks/api/projects/useAddProjectMember";
import useRemoveProjectMember from "@/hooks/api/projects/useRemoveProjectMember";
import { PROJECT_STATUS_OPTIONS } from "@/schemas/project/project.schema";
import { PROJECT_STATUS_CONFIG_MAP } from "@/components/crud/table/ProjectTable";
import type { ApiUser } from "@/types/shift-assignment";
import type { Project, ProjectStatus } from "@/types/project";
import type { UserProjectFormValues } from "@/schemas/project/user-project.schema";

const myProjectsSearchSchema = z.object({
  status: z.enum(PROJECT_STATUS_OPTIONS).optional().catch(undefined),
});

export const Route = createFileRoute("/user/_layout/my-projects")({
  beforeLoad: () => requireFeature("projects"),
  validateSearch: myProjectsSearchSchema,
  component: MyProjects,
});

function MyProjects() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const { page, setPage, pageSize, handlePageSizeChange } = usePaginationState();
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [users, setUsers] = useState<ApiUser[]>([]);

  const projectsQuery = useFetchProjects({
    page,
    page_size: pageSize,
    status: statusFilter ? (statusFilter as ProjectStatus) : undefined,
  });
  const projects = projectsQuery.data?.results ?? [];
  const count = projectsQuery.data?.count ?? 0;

  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const addMember = useAddProjectMember();
  const removeMember = useRemoveProjectMember();

  const isMutating =
    createProject.isPending || updateProject.isPending || deleteProject.isPending;

  useEffect(() => {
    instance
      .get("/api/users/", { params: { page_size: 100 } })
      .then((res) => setUsers(res.data?.results ?? res.data ?? []))
      .catch(() => toast.error("Failed to load users"));
  }, []);

  const handleSubmit = async (data: UserProjectFormValues) => {
    const { member_ids, ...payload } = data;
    if (editing) {
      await updateProject.mutateAsync({ id: editing.id, payload });
      const currentMemberIds = editing.memberships.map((m) => m.user.id);
      const nextMemberIds = member_ids ?? [];
      const toAdd = nextMemberIds.filter((id) => !currentMemberIds.includes(id));
      const toRemove = currentMemberIds.filter((id) => !nextMemberIds.includes(id));
      await Promise.all([
        ...toAdd.map((userId) => addMember.mutateAsync({ id: editing.id, userId })),
        ...toRemove.map((userId) => removeMember.mutateAsync({ id: editing.id, userId })),
      ]);
      toast.success("Project updated successfully!");
    } else {
      const created = await createProject.mutateAsync({ payload });
      await Promise.all(
        (member_ids ?? []).map((userId) =>
          addMember.mutateAsync({ id: created.id, userId }),
        ),
      );
      toast.success("Project created successfully!");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteProject.mutateAsync({ id: deleteTarget.id });
    toast.success("Project deleted successfully!");
    setDeleteTarget(null);
  };

  const handleStatusChange = async (project: Project, status: ProjectStatus) => {
    await updateProject.mutateAsync({ id: project.id, payload: { status } });
    toast.success("Project updated successfully!");
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="My Projects"
          description="Projects you own or are a member of."
          onToggleFilter={() => setShowFilters((s) => !s)}
          onAdd={() => setIsSheetOpen(true)}
          addLabel="New Project"
          isLoading={projectsQuery.isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => setStatusFilter("")}
          isLoading={projectsQuery.isFetching}
        >
          <SelectFilter
            placeholder="Status"
            options={PROJECT_STATUS_OPTIONS.map((s) => ({
              label: PROJECT_STATUS_CONFIG_MAP[s].label,
              value: s,
            }))}
            value={statusFilter as ProjectStatus | undefined}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
        </FiltersPanel>

        <MyProjectTable
          projects={projects}
          isLoading={isMutating || projectsQuery.isPending}
          onTitleClick={(p) =>
            navigate({ to: "/user/my-projects/$id", params: { id: String(p.id) } })
          }
          onView={(p) => setViewingProject(p)}
          onEdit={(p) => {
            setEditing(p);
            setIsSheetOpen(true);
          }}
          onDelete={(p) => setDeleteTarget(p)}
          onStart={(p) => handleStatusChange(p, "active")}
          onHold={(p) => handleStatusChange(p, "on_hold")}
          onResume={(p) => handleStatusChange(p, "active")}
          onComplete={(p) => handleStatusChange(p, "completed")}
          onCancel={(p) => handleStatusChange(p, "cancelled")}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={projectsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <UserProjectFormSheet
        isOpen={isSheetOpen}
        onClose={() => {
          setIsSheetOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
        isLoading={isMutating}
        users={users}
        editingProject={editing}
      />

      {viewingProject && (
        <ProjectDetailsSheet
          isOpen={true}
          onClose={() => setViewingProject(null)}
          project={viewingProject}
          onChangeStatus={(status) => handleStatusChange(viewingProject, status)}
          actionLoading={updateProject.isPending}
          canManage={viewingProject.owner?.id === currentUser?.user_id}
        />
      )}

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete project?"
        description="This cannot be undone. Tasks linked to this project will be unlinked, not deleted."
        itemName={deleteTarget?.name}
        isLoading={deleteProject.isPending}
      />
    </SidebarInset>
  );
}
